// ============================================
// PlaidBankConnect Component
// Uses your existing axiosInstance, AuthContext, MessageContext
//
// Required: npm install react-plaid-link
// ============================================
import React, { useState, useEffect, useCallback } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import axiosInstance from '../utils/axiosInstance';
import { useMessage } from '../contexts/MessageContext';
import {
  Box, Button, Typography, Paper, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton,
  CircularProgress, Alert, Tabs, Tab, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import {
  AccountBalance, CreditCard, Sync, Link as LinkIcon,
  LinkOff, Schedule, Security, Visibility, Add
} from '@mui/icons-material';

export default function PlaidBankConnect() {
  const { showMessage } = useMessage();

  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [totalTxns, setTotalTxns] = useState(0);
  const [linkToken, setLinkToken] = useState(null);
  const [linkError, setLinkError] = useState('');
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);
  const [isLoadingTxns, setIsLoadingTxns] = useState(false);
  const [isExchanging, setIsExchanging] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [disconnectingId, setDisconnectingId] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [disconnectDialog, setDisconnectDialog] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Token is now auto-attached by axiosInstance interceptor

  // ── Fetch Link Token ──
  const fetchLinkToken = useCallback(async () => {
    try {
      const res = await axiosInstance.post('/plaid/create-link-token');
      if (res.data.success) {
        setLinkToken(res.data.data.link_token);
      }
    } catch (err) {
      console.error('Failed to create link token:', err);
    }
  }, []);

  // ── Load Data ──
  const loadAccounts = useCallback(async () => {
    setIsLoadingAccounts(true);
    try {
      const res = await axiosInstance.get('/plaid/accounts');
      setAccounts(res.data.data || []);
    } catch (err) {
      console.error('Failed to load accounts:', err);
    } finally {
      setIsLoadingAccounts(false);
    }
  }, []);

  const loadTransactions = useCallback(async (p = page, ps = pageSize) => {
    setIsLoadingTxns(true);
    try {
      const offset = p * ps;
      const res = await axiosInstance.get(`/plaid/transactions?limit=${ps}&offset=${offset}`);
      setTransactions(res.data.data?.transactions || []);
      setTotalTxns(res.data.data?.total || 0);
    } catch (err) {
      console.error('Failed to load transactions:', err);
    } finally {
      setIsLoadingTxns(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchLinkToken();
    loadAccounts();
  }, [fetchLinkToken, loadAccounts]);

  // Reload transactions when page or pageSize changes
  useEffect(() => {
    loadTransactions(page, pageSize);
  }, [page, pageSize]);

  // ── Plaid Link Callbacks ──
  const onPlaidSuccess = useCallback(async (publicToken, metadata) => {
    setIsExchanging(true);
    try {
      const res = await axiosInstance.post('/plaid/exchange-token', {
        public_token: publicToken,
        institution: metadata.institution,
        accounts: metadata.accounts,
      });

      if (res.data.success) {
        showMessage('success', res.data.message || 'Bank connected successfully!');
        loadAccounts();
        loadTransactions();
      } else {
        showMessage('error', res.data.message || 'Failed to connect');
      }
    } catch (err) {
      showMessage('error', err.response?.data?.message || 'Failed to connect account');
    } finally {
      setIsExchanging(false);
    }
  }, [showMessage, loadAccounts, loadTransactions]);

  const onPlaidExit = useCallback((err) => {
    if (err) {
      console.warn('Plaid Link exited with error:', err);
    }
  }, []);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: onPlaidExit,
  });

  // ── Sync ──
  const handleSync = async () => {
    setIsSyncing(true);
    try {
      const res = await axiosInstance.post('/plaid/sync');
      if (res.data.success) {
        const total = res.data.data?.reduce((s, r) => s + r.added, 0) || 0;
        showMessage('success', `Sync complete — ${total} new transactions`);
        loadTransactions();
      }
    } catch (err) {
      showMessage('error', err.response?.data?.message || 'Sync failed');
    } finally {
      setIsSyncing(false);
    }
  };

  // ── Disconnect ──
  const handleDisconnect = async () => {
    const itemId = disconnectDialog;
    setDisconnectDialog(null);
    setDisconnectingId(itemId);
    try {
      const res = await axiosInstance.delete(`/plaid/accounts/${itemId}`);
      if (res.data.success) {
        showMessage('success', 'Account disconnected');
        loadAccounts();
      }
    } catch (err) {
      showMessage('error', err.response?.data?.message || 'Failed to disconnect');
    } finally {
      setDisconnectingId(null);
    }
  };

  // ── Helpers ──
  const formatCurrency = (amount, code = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code || 'USD',
    }).format(Math.abs(amount));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00')).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  const accountTypeIcon = (type) => {
    if (type === 'credit') return <CreditCard fontSize="small" />;
    return <AccountBalance fontSize="small" />;
  };

  // ============================================
  return (
    <Box className="p-4 md:p-6 max-w-5xl mx-auto">

      {/* Header */}
      <Box className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <Box>
          <Typography variant="h5" fontWeight={700}>Bank Connections</Typography>
          <Typography variant="body2" color="text.secondary">
            Connect your accounts to automatically import transactions nightly.
          </Typography>
        </Box>
        <Box className="flex gap-2">
          <Button
            variant="outlined"
            startIcon={isSyncing ? <CircularProgress size={16} /> : <Sync />}
            onClick={handleSync}
            disabled={isSyncing || !accounts.length}
            size="small"
          >
            {isSyncing ? 'Syncing…' : 'Sync Now'}
          </Button>
          <Button
            variant="contained"
            startIcon={isExchanging ? <CircularProgress size={16} color="inherit" /> : <Add />}
            onClick={() => open()}
            disabled={!ready || isExchanging}
            size="small"
          >
            {isExchanging ? 'Connecting…' : 'Connect Bank'}
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          label={
            <Box className="flex items-center gap-2">
              Connected Accounts
              {accounts.length > 0 && (
                <Chip label={accounts.length} size="small" color="primary" sx={{ height: 20, fontSize: 12 }} />
              )}
            </Box>
          }
        />
        <Tab
          label={
            <Box className="flex items-center gap-2">
              Transactions
              {totalTxns > 0 && (
                <Chip label={totalTxns} size="small" color="primary" sx={{ height: 20, fontSize: 12 }} />
              )}
            </Box>
          }
        />
      </Tabs>

      {/* Accounts Tab */}
      {activeTab === 0 && (
        <>
          {isLoadingAccounts ? (
            <Box className="flex justify-center py-16"><CircularProgress /></Box>
          ) : accounts.length === 0 ? (
            <Paper className="p-8 text-center" variant="outlined">
              <AccountBalance sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>No accounts connected</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Link your bank or credit card to start importing transactions automatically.
              </Typography>
              <Button
                variant="contained"
                startIcon={<LinkIcon />}
                onClick={() => open()}
                disabled={!ready || isExchanging}
              >
                Connect Your First Account
              </Button>
              {linkError && (
                <Alert severity="error" sx={{ mt: 3, justifyContent: 'center' }}>
                  {linkError}
                </Alert>
              )}
            </Paper>
          ) : (
            <Box className="flex flex-col gap-4">
              {accounts.map((acc) => (
                <Paper key={acc.item_id} variant="outlined" className="overflow-hidden">
                  {/* Institution header */}
                  <Box className="flex items-center justify-between p-4">
                    <Box className="flex items-center gap-3">
                      <Box className="w-10 h-10 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold text-lg">
                        {acc.institution_name?.[0]?.toUpperCase() || 'B'}
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {acc.institution_name || 'Unknown Bank'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Connected {formatDate(acc.created_at)}
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      color="error"
                      size="small"
                      startIcon={disconnectingId === acc.item_id ? <CircularProgress size={14} /> : <LinkOff />}
                      onClick={() => setDisconnectDialog(acc.item_id)}
                      disabled={disconnectingId === acc.item_id}
                    >
                      Disconnect
                    </Button>
                  </Box>

                  {/* Sub-accounts */}
                  {acc.accounts?.length > 0 && (
                    <Box className="border-t px-4 py-3 bg-gray-50" sx={{ borderColor: 'divider' }}>
                      <Box className="flex flex-col gap-2">
                        {acc.accounts.map((sub) => (
                          <Box key={sub.id} className="flex items-center gap-3 p-2 rounded bg-white border" sx={{ borderColor: 'divider' }}>
                            {accountTypeIcon(sub.type)}
                            <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>{sub.name}</Typography>
                            <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                              ••••{sub.mask}
                            </Typography>
                            <Chip label={sub.subtype} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Sync info */}
                  <Box className="border-t px-4 py-2" sx={{ borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">
                      <Schedule sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                      Last synced: {formatDate(acc.updated_at)}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </>
      )}

      {/* Transactions Tab */}
      {activeTab === 1 && (
        <>
          {isLoadingTxns ? (
            <Box className="flex justify-center py-16"><CircularProgress /></Box>
          ) : transactions.length === 0 ? (
            <Paper className="p-8 text-center" variant="outlined">
              <Visibility sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>No transactions yet</Typography>
              <Typography variant="body2" color="text.secondary">
                Connect a bank account and transactions will appear here after the first sync.
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Box className="flex items-center justify-between px-4 py-2 border-b" sx={{ borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary">
                  {totalTxns} transaction{totalTxns !== 1 ? 's' : ''}
                </Typography>
                <Box className="flex items-center gap-2">
                  <Typography variant="body2" color="text.secondary">Rows per page:</Typography>
                  <select
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
                    className="border rounded px-2 py-1 text-sm bg-white"
                    style={{ minWidth: 60 }}
                  >
                    {[10, 25, 50, 100].map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </Box>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell><strong>Category</strong></TableCell>
                    <TableCell align="right"><strong>Amount</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Reconciled</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((txn) => (
                    <TableRow
                      key={txn.plaid_transaction_id || txn.uuid}
                      sx={{ opacity: txn.pending ? 0.6 : 1 }}
                      hover
                    >
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace" fontSize={13}>
                          {formatDate(txn.date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {txn.merchant_name || txn.name}
                        </Typography>
                        {txn.merchant_name && txn.name !== txn.merchant_name && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {txn.name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {txn.category && (
                          <Chip
                            label={Array.isArray(txn.category) ? txn.category[0] : txn.category}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: 11 }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          fontFamily="monospace"
                          color={txn.amount < 0 ? 'success.main' : 'error.main'}
                        >
                          {txn.amount < 0 ? '+' : '-'}{formatCurrency(txn.amount, txn.iso_currency_code)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={txn.pending ? 'Pending' : 'Posted'}
                          size="small"
                          color={txn.pending ? 'warning' : 'success'}
                          sx={{ fontSize: 11 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={txn.reconciliation_status || 'unmatched'}
                          size="small"
                          color={
                            txn.reconciliation_status === 'matched' ? 'success'
                            : txn.reconciliation_status === 'created' ? 'info'
                            : txn.reconciliation_status === 'ignored' ? 'default'
                            : 'warning'
                          }
                          sx={{ fontSize: 10, textTransform: 'capitalize' }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {/* Pagination controls */}
              <Box className="flex items-center justify-between px-4 py-3 border-t" sx={{ borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {totalTxns === 0 ? 0 : page * pageSize + 1}–{Math.min((page + 1) * pageSize, totalTxns)} of {totalTxns}
                </Typography>
                <Box className="flex items-center gap-1">
                  <Button
                    size="small"
                    disabled={page === 0}
                    onClick={() => setPage(0)}
                  >
                    First
                  </Button>
                  <Button
                    size="small"
                    disabled={page === 0}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Previous
                  </Button>
                  <Chip
                    label={`Page ${page + 1} of ${Math.max(1, Math.ceil(totalTxns / pageSize))}`}
                    size="small"
                    variant="outlined"
                    sx={{ mx: 1 }}
                  />
                  <Button
                    size="small"
                    disabled={(page + 1) * pageSize >= totalTxns}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                  <Button
                    size="small"
                    disabled={(page + 1) * pageSize >= totalTxns}
                    onClick={() => setPage(Math.ceil(totalTxns / pageSize) - 1)}
                  >
                    Last
                  </Button>
                </Box>
              </Box>
            </TableContainer>
          )}
        </>
      )}

      {/* Info Footer */}
      <Box className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8 pt-6 border-t" sx={{ borderColor: 'divider' }}>
        {[
          { icon: <Security color="primary" />, title: 'Bank-grade security', desc: 'Credentials never touch our servers. Secured via Plaid.' },
          { icon: <Schedule color="primary" />, title: 'Automatic nightly sync', desc: 'Transactions are refreshed every night at 2:00 AM.' },
          { icon: <Visibility color="primary" />, title: 'Read-only access', desc: 'We can only view transactions — no transfers or changes.' },
        ].map((item) => (
          <Box key={item.title} className="flex gap-3">
            {item.icon}
            <Box>
              <Typography variant="body2" fontWeight={600}>{item.title}</Typography>
              <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Disconnect Confirmation Dialog */}
      <Dialog open={!!disconnectDialog} onClose={() => setDisconnectDialog(null)}>
        <DialogTitle>Disconnect Account?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove the connection to your bank. Existing imported transactions will be kept.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisconnectDialog(null)}>Cancel</Button>
          <Button onClick={handleDisconnect} color="error" variant="contained">Disconnect</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
